import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertStaff(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) throw new Error("Forbidden");
  return { isAdmin: roles.includes("admin") };
}
/** form_submissions.client_id / cases.client_id reference profiles.id, not auth.uid(). */
async function getProfileId(supabase: any, userId: string): Promise<string | null> {
  const { data } = await supabase.from("profiles").select("id").eq("user_id", userId).maybeSingle();
  return data?.id ?? null;
}
async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden: admin only");
}
const FIELD_TYPES = ["short_text", "long_text", "single_choice", "multi_choice", "scale", "date", "email", "phone", "number", "file"] as const;

const fieldSchema: z.ZodTypeAny = z.object({
  id: z.string().min(1),
  type: z.enum(FIELD_TYPES),
  label_ar: z.string().min(1),
  help_ar: z.string().optional(),
  required: z.boolean().default(false),
  options: z.array(z.string()).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
});
// Phase 3 — schema may be legacy `{ fields: [...] }` OR sections-based
// `{ sections: [{ items, visible_when? }, ...] }`. Accept both; unknown
// keys pass through so `visible_when`, `_content_status`, `_placeholder`,
// etc. survive admin edits.
const schemaShape = z
  .object({ fields: z.array(fieldSchema).default([]) })
  .passthrough();

/**
 * Phase 3 — determine whether a section is visible given current answers.
 * A section is visible if it has no `visible_when` gate, or if the current
 * answer for the gate item includes any of the required values. Handles
 * both single-choice (string) and multi-choice (string[]) answers.
 */
export function isSectionVisible(section: any, answers: Record<string, any>): boolean {
  const gate = section?.visible_when;
  if (!gate || !gate.item_id) return true;
  const values: string[] = Array.isArray(gate.values) ? gate.values : [];
  if (values.length === 0) return true;
  const v = answers?.[gate.item_id];
  if (v === undefined || v === null || v === "") return false;
  if (Array.isArray(v)) return v.some((x) => values.includes(x));
  return values.includes(String(v));
}

/**
 * Phase 3 — strip answers keyed to hidden-section items. Deterministic
 * because gate items live in visible sections (main_problem is required
 * on s1_child_info, which never has visible_when).
 */
export function stripHiddenSectionAnswers(schema: any, answers: Record<string, any>): Record<string, any> {
  const sections: any[] = schema?.sections ?? [];
  if (sections.length === 0) return { ...answers };
  const hiddenItemIds = new Set<string>();
  for (const sec of sections) {
    if (!isSectionVisible(sec, answers)) {
      for (const it of sec.items ?? []) if (it?.id) hiddenItemIds.add(it.id);
    }
  }
  if (hiddenItemIds.size === 0) return { ...answers };
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(answers ?? {})) {
    if (!hiddenItemIds.has(k)) out[k] = v;
  }
  return out;
}

// ---------- Admin ----------
export const listFormsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const { data: forms, error } = await context.supabase
      .from("forms")
      .select("id, slug, title_ar, description_ar, audience, category, is_active, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw error;
    if (!forms?.length) return [];
    const ids = forms.map((f: any) => f.id);
    const { data: subs } = await context.supabase.from("form_submissions").select("form_id").in("form_id", ids);
    const countByForm = new Map<string, number>();
    (subs ?? []).forEach((s: any) => countByForm.set(s.form_id, (countByForm.get(s.form_id) ?? 0) + 1));
    return forms.map((f: any) => ({ ...f, submission_count: countByForm.get(f.id) ?? 0 }));
  });

export const getFormAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const { data: form, error } = await context.supabase.from("forms").select("*").eq("id", data.id).maybeSingle();
    if (error) throw error;
    return form;
  });

const formUpsertInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(2).regex(/^[a-z0-9-]+$/),
  title_ar: z.string().min(2),
  description_ar: z.string().optional().default(""),
  audience: z.enum(["client", "staff", "public"]).default("client"),
  category: z.string().nullable().optional(),
  schema: schemaShape,
  is_active: z.boolean().default(false),
});

export const upsertForm = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => formUpsertInput.parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const patch: any = {
      slug: data.slug,
      title_ar: data.title_ar,
      description_ar: data.description_ar,
      audience: data.audience,
      category: data.category ?? null,
      schema: data.schema,
      is_active: data.is_active,
    };
    if (data.id) {
      const { error } = await context.supabase.from("forms").update(patch).eq("id", data.id);
      if (error) throw error;
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase.from("forms").insert(patch).select("id").single();
    if (error) throw error;
    return { id: row.id };
  });

export const deleteForm = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("forms").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// ---------- Client fill ----------
export const getFormForFill = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { slug: string }) => z.object({ slug: z.string() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: form, error } = await context.supabase
      .from("forms")
      .select("id, slug, title_ar, description_ar, audience, schema, is_active")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw error;
    if (!form || !form.is_active) return null;
    return form;
  });

function validateField(f: any, answers: any): string | null {
  const v = answers?.[f.id];
  if (f.required && (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0))) {
    return `الحقل "${f.label_ar}" مطلوب`;
  }
  if (v == null || v === "") return null;
  if (f.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v))) return `بريد إلكتروني غير صحيح: ${f.label_ar}`;
  if (f.type === "number" || f.type === "scale") {
    const n = Number(v);
    if (Number.isNaN(n)) return `قيمة رقمية غير صحيحة: ${f.label_ar}`;
    if (typeof f.min === "number" && n < f.min) return `${f.label_ar} أقل من الحد الأدنى`;
    if (typeof f.max === "number" && n > f.max) return `${f.label_ar} أكبر من الحد الأعلى`;
  }
  if (f.type === "single_choice" && !(f.options ?? []).includes(v)) return `اختيار غير صحيح: ${f.label_ar}`;
  if (f.type === "multi_choice") {
    if (!Array.isArray(v)) return `اختيار واحد على الأقل مطلوب: ${f.label_ar}`;
    for (const item of v) if (!(f.options ?? []).includes(item)) return `اختيار غير صحيح: ${f.label_ar}`;
  }
  return null;
}

// Phase 3 — sections-aware validation. Walks legacy `fields[]` OR
// `sections[].items[]`; skips required-checks for items in sections hidden
// by `visible_when` (main_problem gate item lives in a visible section).
function validateAnswers(schema: any, answers: any): string | null {
  const sections: any[] = schema?.sections ?? [];
  if (sections.length > 0) {
    for (const sec of sections) {
      if (!isSectionVisible(sec, answers)) continue;
      for (const it of sec.items ?? []) {
        if (it?.visible_when && !isSectionVisible({ visible_when: it.visible_when }, answers)) continue;
        const err = validateField(it, answers);
        if (err) return err;
      }
    }
    return null;
  }
  const fields = (schema?.fields ?? []) as any[];
  for (const f of fields) {
    const err = validateField(f, answers);
    if (err) return err;
  }
  return null;
}

export const submitForm = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { slug: string; answers: any; linked_entity?: string; linked_entity_id?: string }) =>
    z.object({
      slug: z.string(),
      answers: z.record(z.any()),
      linked_entity: z.string().nullable().optional(),
      linked_entity_id: z.string().uuid().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { data: form, error } = await context.supabase
      .from("forms")
      .select("id, schema, is_active")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw error;
    if (!form || !form.is_active) throw new Error("Form not available");
    // Phase 3 — strip hidden-section answers before persisting so a branch
    // the user backed out of doesn't leak into downstream code.
    const cleanedAnswers = stripHiddenSectionAnswers(form.schema, data.answers ?? {});
    const err = validateAnswers(form.schema, cleanedAnswers);
    if (err) throw new Error(err);

    // client_id on form_submissions references profiles.id, not auth.uid().
    const profileId = await getProfileId(context.supabase, context.userId);

    // If linking to a case, ensure caller owns that case
    if (data.linked_entity === "case" && data.linked_entity_id) {
      const { data: caseRow } = await context.supabase
        .from("cases")
        .select("client_id")
        .eq("id", data.linked_entity_id)
        .maybeSingle();
      const isOwner = !!profileId && caseRow?.client_id === profileId;
      const { data: rolesRow } = await context.supabase.from("user_roles").select("role").eq("user_id", context.userId);
      const isStaff = (rolesRow ?? []).some((r: any) => r.role === "admin" || r.role === "assistant");
      if (!isOwner && !isStaff) throw new Error("Forbidden");
    }

    const { data: row, error: insErr } = await context.supabase
      .from("form_submissions")
      .insert({
        form_id: form.id,
        client_id: profileId,
        answers: cleanedAnswers,
        form_snapshot: form.schema,
        linked_entity: data.linked_entity ?? null,
        linked_entity_id: data.linked_entity_id ?? null,
      })
      .select("id")
      .single();
    if (insErr) throw insErr;
    return { id: row.id };
  });

// ---------- Case-linked submissions ----------
export const listCaseFormSubmissions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { case_id: string }) => z.object({ case_id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: caseRow } = await context.supabase
      .from("cases")
      .select("client_id")
      .eq("id", data.case_id)
      .maybeSingle();
    const profileId = await getProfileId(context.supabase, context.userId);
    const isOwner = !!profileId && caseRow?.client_id === profileId;
    const { data: rolesRow } = await context.supabase.from("user_roles").select("role").eq("user_id", context.userId);
    const isStaff = (rolesRow ?? []).some((r: any) => r.role === "admin" || r.role === "assistant");
    if (!isOwner && !isStaff) throw new Error("Forbidden");
    const { data: subs, error } = await context.supabase
      .from("form_submissions")
      .select("id, form_id, client_id, answers, form_snapshot, created_at")
      .eq("linked_entity", "case")
      .eq("linked_entity_id", data.case_id)
      .order("created_at", { ascending: false });
    if (error) throw error;
    const formIds = Array.from(new Set((subs ?? []).map((s: any) => s.form_id)));
    const { data: forms } = formIds.length
      ? await context.supabase.from("forms").select("id, title_ar, slug").in("id", formIds)
      : { data: [] as any[] };
    const formById = new Map((forms ?? []).map((f: any) => [f.id, f]));
    return (subs ?? []).map((s: any) => ({ ...s, form: formById.get(s.form_id) ?? null }));
  });

export const getSubmission = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: sub, error } = await context.supabase
      .from("form_submissions")
      .select("*, forms(title_ar, slug)")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!sub) return null;
    const profileId = await getProfileId(context.supabase, context.userId);
    if (!profileId || sub.client_id !== profileId) {
      await assertStaff(context.supabase, context.userId);
    }
    return sub;
  });

// ---------- Portal: forms available/pending for current user ----------
export const listMyPendingForms = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: forms, error } = await context.supabase
      .from("forms")
      .select("id, slug, title_ar, category, audience, is_active")
      .eq("is_active", true)
      .in("audience", ["client"]);
    if (error) throw error;
    if (!forms?.length) return [];
    const profileId = await getProfileId(context.supabase, context.userId);
    if (!profileId) return forms;
    const { data: mySubs } = await context.supabase
      .from("form_submissions")
      .select("form_id")
      .eq("client_id", profileId)
      .is("linked_entity_id", null);
    const submittedIds = new Set((mySubs ?? []).map((s: any) => s.form_id));
    return forms.filter((f: any) => !submittedIds.has(f.id));
  });
